import React from "react";
import { Loading } from "components/panels";
import { CommonSignatoryFieldProps, ContactField, DebugField, EmailField, LanguageField, RepresentingField, SignNowField, UserField } from "./SignatoryFields";
import { InternalUserOrContactDetailSection } from "./InternalUserOrContact";
import { Section } from "components/MainSectionTemplate/types";
import SectionPanel from "components/panels/SectionPanel/SectionPanel";
import PanelSegment from "components/panels/PanelSegment/PanelSegment";
import ESignParticipantProgressBadge from "eSign/components/EsignParticipantProgressBadge";
import { PhosphorXButton } from "components/Atoms/PhosphorIconButton";
import { useContact } from "utilities/api/contactQuery";
import { useCurrentUser } from "common/SessionContext";

interface Props {
  esignDocument: server.dto.ESignDocument
  sender: server.dto.ESignParticipant;
  progress: server.dto.ESignParticipantProgress

  draft?: {
    bookkeeping: server.dto.BookkeepingSmall;
    onSenderChange: (sender: server.dto.ESignParticipant) => void;
    onRemoveSender: () => void;
    isRemovingSender: boolean;
    noSenderError: boolean;
    onCompanyChange?: () => void;
  }
}

export const SenderPanel = (props: Props): Section => {
  const currentUser = useCurrentUser();
  const hasSender = props.sender != null;
  const senderIsInternalUser = hasSender && props.sender.linkedToUser != null;
  const editDisabled = props.esignDocument.lifeCycle.value !== "draft";

  const canDeleteParticipant = !editDisabled && props.sender != null && !props.draft?.isRemovingSender;

  const contact = useContact(props.sender?.contact);

  const fieldProps: CommonSignatoryFieldProps = {
    esignDocument: props.esignDocument,
    participant: props.sender,
    role: "sender",
    hasParticipant: hasSender,
    editDisabled,
    progress: props.progress,
    contact
  }

  if (!hasSender) {
    return {
      key: "sender",
      type: "custom",
      render: () => <SectionPanel.Panel
        id="senderPanel"
        header={<PanelSegment.Title title={RESX.ESign.Sender} />}
      >
        <InternalUserOrContactDetailSection
          esignDocument={props.esignDocument}
          bookkeeping={props.draft.bookkeeping}
          onSenderChange={props.draft.onSenderChange}
          role="sender"
          noSenderError={props.draft.noSenderError}
        />
      </SectionPanel.Panel>,
    }
  }

  return {
    key: "sender",
    title: <div id="senderPanel" className="flex justify-space-between">
      {RESX.ESign.Sender}
      {props.sender != null && <div>
        {canDeleteParticipant && <PhosphorXButton
          layout="in-square"
          onClick={props.draft.onRemoveSender}
          iconColor="error"
        />}
        {props.draft?.isRemovingSender && <Loading size="tiny" />}
        <ESignParticipantProgressBadge signedUtc={props.sender.signature?.signedUtc} progress={props.progress} />
      </div>}
    </div>,
    type: "regular",
    fields: senderIsInternalUser ? [
      UserField(fieldProps),
      RepresentingField({ ...fieldProps, onCompanyChange: props.draft?.onCompanyChange }),
      DebugField(fieldProps),
      SignNowField(fieldProps, currentUser)
    ] : [
      ContactField({ ...fieldProps, hasError: false, senderSigner: "sender" }),
      RepresentingField({ ...fieldProps, onCompanyChange: props.draft?.onCompanyChange }),
      EmailField(fieldProps),
      LanguageField(fieldProps),
      DebugField(fieldProps),
      SignNowField(fieldProps, currentUser)
    ],
  }


}


